import { createClient, mapExchange, ssrExchange, cacheExchange, fetchExchange, Client, subscriptionExchange } from '@urql/core';
import { type AuthConfig, type AuthUtilities, authExchange } from '@urql/exchange-auth';
import { defineNuxtPlugin } from '#app'
import { ref } from "vue";
import {trackJourney} from '~/utils/journey-client.mjs';
import {apiBase} from '~/utils/api-base.mjs';
import { createClient as createWSClient } from 'graphql-ws';

const ssrKey = '__URQL_DATA__'

export default defineNuxtPlugin(nuxt => {
  const { vueApp } = nuxt
  const config = useRuntimeConfig();
  const reviewGeneration=useState('plant-review-generation',()=>0);
  const refreshedQueries=new Map<number,number>();
  const ratingStage=!!config.public.RATINGS_STAGE;
  const preview=ratingStage||String(config.public.ACHIEVEMENTS_PREVIEW)==='1'||config.public.ACHIEVEMENTS_PREVIEW===true;

  const ssr = ssrExchange({
    isClient: process.client
  })

  // when app is created in browser, restore SSR state from nuxt payload
  if (process.client) {
    nuxt.hook('app:created', () => {
      ssr.restoreData(nuxt.payload[ssrKey] as any)
    })
  }

  // when app has rendered in server, send SSR state to client
  if (process.server) {
    nuxt.hook('app:rendered', (event) => {
      nuxt.payload[ssrKey] = ssr.extractData()
    })
  }

  const wsClient = process.client && !preview ? createWSClient({
    url: `${config.public.API_BASE_URL}/v1/graphql`,
    retryAttempts: Infinity,
    shouldRetry: () => true,
    keepAlive: 10000,
  }) : null;

  const relay=ratingStage?'/api/ratings-stage-query':'/api/achievements-preview-query';
  const client = createClient({
    url: preview ? (process.server ? `http://127.0.0.1:${process.env.PORT||3000}${relay}` : relay) : `${apiBase(config, process.server)}/v1/graphql`,
    // requestPolicy: 'cache-and-network',
    fetchOptions: {
      credentials: 'include',
    },
    exchanges: [
        mapExchange({onOperation(operation){
          const name=(operation.query.definitions.find((d:any)=>d.kind==='OperationDefinition')as any)?.name?.value||'';
          const version=reviewGeneration.value;
          if(process.client&&version>0&&operation.kind==='query'&&/Seed|Catalog|Review|Brand/.test(name)&&refreshedQueries.get(operation.key)!==version){
            refreshedQueries.set(operation.key,version);return {...operation,context:{...operation.context,requestPolicy:'network-only'}};
          }
          return operation;
        }}),
        cacheExchange,
        mapExchange({
          onResult(result) {
            if (process.client && result.operation.kind === 'mutation' && !result.error && result.data) {
              const name = result.operation.query.definitions.find((node:any) => node.kind === 'OperationDefinition')?.name?.value;
              if ((name === 'InsertPlant' && result.data.insert_uogorod_garden_seeds?.returning?.length)
                || (name === 'UpdateGardenSeed' && result.data.update_uogorod_garden_seeds_by_pk?.id)) {
                void trackJourney('observe');
              }
            }
            if (result.error && process.client) {
              addNotification({
                tag: 'warn',
                title: result.operation.query.definitions[0].name.value,
                body: result.error.message,
                timeout: 0,
                actions: [
                  { title: 'Закрыть' }
                ]
              });
            }
          },
          onError(error, _operation) {
            console.log(error, _operation);
            const isAuthError = error.graphQLErrors.some(e => e.extensions?.code === 'invalid-jwt');
          },
        }),
        authExchange(async (utils:AuthUtilities):Promise<AuthConfig> => {
          return {
            // FIXME 404, когда есть протухший access_token
            addAuthToOperation(operation) {
              // Browsers send the HttpOnly cookie themselves; only SSR needs forwarding.
              if (process.client || ratingStage) return operation;
              const accessToken = useCookie('access_token', { readonly: true }).value;

              if (!accessToken || ratingStage) return operation;

              return utils.appendHeaders(operation, {
                Cookie: `access_token=${accessToken}`
              });
            },
            didAuthError(error, _operation) {
              return error.graphQLErrors.some(e => e.extensions?.code === 'invalid-jwt');
            },
            async refreshAuth() {
              try {
                if (process.client) {
                  await $fetch('/api/auth/refresh', { method: 'POST' });
                }
              } catch(error) {
                console.log('error', error);
              }
            }
          };
        }),
        ssr, // Add `ssr` in front of the `fetchExchange`
        fetchExchange,
        subscriptionExchange({
          forwardSubscription(request) {
            const input = { ...request, query: request.query || '' };
            return {
              subscribe(sink) {
                if(!wsClient){sink.complete();return {unsubscribe(){}};}
                const unsubscribe = wsClient.subscribe(input, sink);
                return { unsubscribe };
              },
            };
          },
        })
    ],
  })

  nuxt.provide('urql', client)
  vueApp.provide('$urql', ref(client))
})

declare module '#app' {
  interface NuxtApp {
    $urql: Client
  }
}
